import React, { useEffect, useState } from 'react';
import { DashboardApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { notifySuccess } from '../utils/statusShortcuts';
import './GoogleCalendarPage.css';

const GoogleCalendarPage = () => {
  const { selectedCompany, user } = useAuth();
  const companyId = selectedCompany?.id || null;
  const isOwner = Boolean(selectedCompany?.isOwner || selectedCompany?.isSuperAdminAccess || user?.isAdmin);

  const [googleConfig, setGoogleConfig] = useState({
    configured: false,
    client_email: '',
    private_key: '',
  });
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleSaving, setGoogleSaving] = useState(false);
  const [googleStatus, setGoogleStatus] = useState(null);

  useEffect(() => {
    if (!companyId || !isOwner) {
      setGoogleConfig({ configured: false, client_email: '', private_key: '' });
      setGoogleStatus(null);
      return;
    }
    let mounted = true;
    (async () => {
      setGoogleLoading(true);
      try {
        const data = await DashboardApi.getGoogleServiceAccount(companyId);
        if (!mounted) return;
        setGoogleConfig({
          configured: data.configured,
          client_email: data.client_email || '',
          private_key: '',
        });
      } catch {
        if (mounted) {
          setGoogleConfig({ configured: false, client_email: '', private_key: '' });
        }
      } finally {
        if (mounted) setGoogleLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [companyId, isOwner]);

  const handleGoogleSave = async () => {
    if (!companyId || !googleConfig.client_email || !googleConfig.private_key) {
      setGoogleStatus({ type: 'error', message: 'E-post og privat nøkkel er påkrevd.' });
      return;
    }
    setGoogleSaving(true);
    setGoogleStatus(null);
    try {
      const result = await DashboardApi.saveGoogleServiceAccount(companyId, {
        client_email: googleConfig.client_email,
        private_key: googleConfig.private_key,
      });
      setGoogleConfig((prev) => ({ ...prev, configured: result.configured, private_key: '' }));
      setGoogleStatus({ type: 'success', message: 'Google Workspace-integrasjon lagret.' });
      notifySuccess('Google Calendar integrasjon lagret.', { source: 'Selskap' });
    } catch (err) {
      setGoogleStatus({ type: 'error', message: err.message || 'Kunne ikke lagre.' });
    } finally {
      setGoogleSaving(false);
    }
  };

  const handleGoogleDelete = async () => {
    if (!companyId) return;
    setGoogleSaving(true);
    setGoogleStatus(null);
    try {
      await DashboardApi.deleteGoogleServiceAccount(companyId);
      setGoogleConfig({ configured: false, client_email: '', private_key: '' });
      setGoogleStatus({ type: 'success', message: 'Google Workspace-integrasjon fjernet.' });
      notifySuccess('Google Calendar integrasjon fjernet.', { source: 'Selskap' });
    } catch (err) {
      setGoogleStatus({ type: 'error', message: err.message || 'Kunne ikke fjerne.' });
    } finally {
      setGoogleSaving(false);
    }
  };

  if (!isOwner) {
    return (
      <div className="page settings-page">
        <div className="settings-restricted">
          <div className="settings-restricted-icon">&#128274;</div>
          <h2>Ingen tilgang</h2>
          <p>Bare eiere kan administrere integrasjoner.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page settings-page">
      <header className="settings-header">
        <div className="settings-header-content">
          <p className="settings-eyebrow">Selskap</p>
          <h1>Google Calendar</h1>
          <p className="settings-subtitle">
            Konfigurer Google Workspace-integrasjon for automatisk synkronisering av kalender og Google Meet.
          </p>
        </div>
      </header>

      <section className="settings-card">
        <header className="settings-card-header">
          <h2>Google Workspace-integrasjon</h2>
          <p>
            Konfigurer en Service Account for domene-delegert tilgang til Google Calendar.
            <a
              href="https://developers.google.com/workspace/guides/create-credentials#service_account"
              target="_blank"
              rel="noopener noreferrer"
              className="google-docs-link"
            >
              Dokumentasjon
            </a>
          </p>
        </header>

        {googleLoading ? (
          <p className="settings-muted">Laster integrasjon...</p>
        ) : (
          <div className="settings-grid">
            {googleConfig.configured && (
              <div className="settings-field full-width">
                <div className="settings-alert info">
                  <span>&#9432;</span>
                  <p>
                    Service Account er konfigurert: <strong>{googleConfig.client_email}</strong>
                  </p>
                </div>
              </div>
            )}

            <div className="settings-field full-width">
              <label htmlFor="googleClientEmail">Service Account E-post (client_email)</label>
              <input
                id="googleClientEmail"
                type="email"
                value={googleConfig.client_email}
                onChange={(e) =>
                  setGoogleConfig((prev) => ({ ...prev, client_email: e.target.value }))
                }
                placeholder="name@project-id.iam.gserviceaccount.com"
                className="input"
              />
            </div>

            <div className="settings-field full-width">
              <label htmlFor="googlePrivateKey">Privat nøkkel (private_key)</label>
              <textarea
                id="googlePrivateKey"
                value={googleConfig.private_key}
                onChange={(e) =>
                  setGoogleConfig((prev) => ({ ...prev, private_key: e.target.value }))
                }
                placeholder="Lim inn private_key fra JSON-filen..."
                rows={4}
                className="settings-textarea"
                spellCheck={false}
              />
              <small className="settings-field-hint">
                Innholdet vises aldri etter lagring.
              </small>
            </div>

            {googleStatus && (
              <div className={`settings-alert ${googleStatus.type}`}>
                <span>{googleStatus.type === 'success' ? '\u2705' : '\u26A0'}</span>
                <p>{googleStatus.message}</p>
              </div>
            )}

            <div className="settings-field full-width">
              <div className="settings-card-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleGoogleSave}
                  disabled={googleSaving}
                >
                  {googleSaving ? 'Lagrer...' : 'Lagre Service Account'}
                </button>
                {googleConfig.configured && (
                  <button
                    type="button"
                    className="btn btn-danger-outline"
                    onClick={handleGoogleDelete}
                    disabled={googleSaving}
                  >
                    Fjern integrasjon
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default GoogleCalendarPage;
